import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  EmailNotificationLog,
  EmailNotificationLogQuery,
  NotificationHealthSummary,
  PagedResult,
  ScheduledJobRun,
  ScheduledJobRunQuery,
  UpcomingReminder
} from '../models/notification-health.model';

@Injectable({ providedIn: 'root' })
export class NotificationHealthService {
  private apiUrl = `${environment.apiUrl}/NotificationHealth`;

  constructor(private http: HttpClient) {}

  getSummary(): Observable<NotificationHealthSummary> {
    return this.http
      .get<NotificationHealthSummary>(`${this.apiUrl}/summary`)
      .pipe(retry(1));
  }

  queryJobRuns(q: ScheduledJobRunQuery): Observable<PagedResult<ScheduledJobRun>> {
    let params = new HttpParams()
      .set('pageNumber', q.pageNumber.toString())
      .set('pageSize', q.pageSize.toString());

    if (q.jobName) params = params.set('jobName', q.jobName);
    if (q.status) params = params.set('status', q.status);
    if (q.fromDate) params = params.set('fromDate', q.fromDate);
    if (q.toDate) params = params.set('toDate', q.toDate);
    if (q.sortField) params = params.set('sortField', q.sortField);
    if (q.sortOrder) params = params.set('sortOrder', q.sortOrder.toString());

    return this.http
      .get<PagedResult<ScheduledJobRun>>(`${this.apiUrl}/job-runs`, { params })
      .pipe(retry(1));
  }

  queryEmailLog(q: EmailNotificationLogQuery): Observable<PagedResult<EmailNotificationLog>> {
    let params = new HttpParams()
      .set('pageNumber', q.pageNumber.toString())
      .set('pageSize', q.pageSize.toString());

    if (q.notificationType) params = params.set('notificationType', q.notificationType);
    // Explicit null/undefined check: `false` is a meaningful value here (failed only).
    if (q.isSent !== undefined && q.isSent !== null) {
      params = params.set('isSent', q.isSent.toString());
    }
    if (q.fromDate) params = params.set('fromDate', q.fromDate);
    if (q.toDate) params = params.set('toDate', q.toDate);
    if (q.search) params = params.set('search', q.search);
    if (q.sortField) params = params.set('sortField', q.sortField);
    if (q.sortOrder) params = params.set('sortOrder', q.sortOrder.toString());

    return this.http
      .get<PagedResult<EmailNotificationLog>>(`${this.apiUrl}/email-log`, { params })
      .pipe(retry(1));
  }

  getUpcoming(daysAhead = 30): Observable<UpcomingReminder[]> {
    const params = new HttpParams().set('daysAhead', daysAhead.toString());
    return this.http
      .get<UpcomingReminder[]>(`${this.apiUrl}/upcoming`, { params })
      .pipe(retry(1));
  }

  getNotificationTypes(): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.apiUrl}/notification-types`)
      .pipe(retry(1));
  }
}
